import { useEffect, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { ROLE_LABELS, PERM_PROFILES, expandPerms } from "@/lib/constants";
import { PageHeader, Loader } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, MoreVertical, Pencil, Ban, Loader2, ShieldCheck, SlidersHorizontal, Trash2, RotateCcw, Printer, KeyRound, MonitorSmartphone } from "lucide-react";
import { printAccessReport } from "@/components/AccessReport";
import { PasswordStrength } from "@/components/PasswordStrength";
import { DevicesPanel } from "@/components/DevicesPanel";
import { toast } from "sonner";
import PermMatrix from "@/components/PermMatrix";

const cleanPerms = (p) => { const c = {}; Object.entries(p || {}).forEach(([k, v]) => { if (v && v !== "default" && !(typeof v === "object" && Object.keys(v).length === 0)) c[k] = v; }); return c; };


export default function Users() {
  const [users, setUsers] = useState(null);
  const [audit, setAudit] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(null);
  const [permU, setPermU] = useState(null);
  const [permForm, setPermForm] = useState({});
  const [customProfiles, setCustomProfiles] = useState([]);
  const [devU, setDevU] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/audit").then((r) => setAudit(r.data)).catch(() => {});
    api.get("/perm-profiles").then((r) => setCustomProfiles(r.data)).catch(() => {});
  }, []);
  const allProfiles = [...PERM_PROFILES, ...customProfiles.map((cp) => ({ value: cp.id, label: cp.label, perms: cp.perms }))];

  const openNew = () => { setF({ name: "", email: "", password: "", role: "collaborateur", function: "", department: "", permissions: {} }); setOpen(true); };
  const openEdit = (u) => { setF({ ...u, password: "", permissions: { ...(u.permissions || {}) } }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (f.id) { const payload = { ...f, permissions: cleanPerms(f.permissions) }; if (!payload.password) delete payload.password; await api.put(`/users/${f.id}`, payload); }
      else await api.post("/users", { ...f, permissions: cleanPerms(f.permissions) });
      toast.success("Utilisateur enregistré"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const deactivate = async (u, active) => {
    try { await api.put(`/users/${u.id}`, { active }); toast.success(active ? "Utilisateur réactivé" : "Utilisateur désactivé"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const sendReset = async (u) => {
    if (!window.confirm(`Envoyer à ${u.email} un lien de réinitialisation du mot de passe (valable 1 heure) ?`)) return;
    try { await api.post(`/users/${u.id}/send-reset`); toast.success(`Lien envoyé à ${u.email}`); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement l'utilisateur « ${u.name} » ? Cette action est irréversible.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utilisateur supprimé"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openPerm = (u) => { setPermForm({ ...(u.permissions || {}) }); setPermU(u); };
  const savePerm = async () => {
    const clean = cleanPerms(permForm);
    try { await api.put(`/users/${permU.id}`, { permissions: clean }); toast.success("Droits mis à jour"); setPermU(null); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div>
      <PageHeader title="Administration" subtitle="Gestion des utilisateurs, rôles & journal d'audit">
        <Button onClick={openNew} data-testid="add-user" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouvel utilisateur</Button>
      </PageHeader>

      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Journal d'audit</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          {users === null ? <Loader /> : (
            <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Département</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="hover:bg-slate-50">
                      <TableCell className="font-semibold flex items-center gap-2">{u.role === "super_admin" && <ShieldCheck className="h-4 w-4 text-brand-600" />}{u.name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                      <TableCell><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs">{ROLE_LABELS[u.role]}</span></TableCell>
                      <TableCell className="text-sm">{u.department || "—"}</TableCell>
                      <TableCell><StatusBadge value={u.active === false ? "Inactif" : "Actif"} /></TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`user-menu-${u.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(u)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                            {u.role !== "super_admin" && <DropdownMenuItem onClick={() => openPerm(u)} data-testid={`user-perms-${u.id}`}><SlidersHorizontal className="h-4 w-4 mr-2" />Droits par module</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => printAccessReport(u.id)} data-testid={`user-access-report-${u.id}`}><Printer className="h-4 w-4 mr-2" />Récapitulatif des accès</DropdownMenuItem>
                            {u.active !== false && <DropdownMenuItem onClick={() => sendReset(u)} data-testid={`user-send-reset-${u.id}`}><KeyRound className="h-4 w-4 mr-2" />Envoyer un lien de réinitialisation</DropdownMenuItem>}
                            <DropdownMenuItem onClick={() => setDevU(u)} data-testid={`user-devices-${u.id}`}><MonitorSmartphone className="h-4 w-4 mr-2" />Appareils</DropdownMenuItem>
                            {u.active === false
                              ? <DropdownMenuItem onClick={() => deactivate(u, true)} data-testid={`user-reactivate-${u.id}`} className="text-emerald-600"><RotateCcw className="h-4 w-4 mr-2" />Réactiver</DropdownMenuItem>
                              : <DropdownMenuItem onClick={() => deactivate(u, false)} data-testid={`user-deactivate-${u.id}`} className="text-amber-600"><Ban className="h-4 w-4 mr-2" />Désactiver</DropdownMenuItem>}
                            {u.role !== "super_admin" && <DropdownMenuItem onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div></div>
          )}
        </TabsContent>

        <TabsContent value="audit">
          <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Date</TableHead><TableHead>Utilisateur</TableHead><TableHead>Action</TableHead><TableHead>Objet</TableHead></TableRow></TableHeader>
              <TableBody>
                {audit.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">Aucune action enregistrée</TableCell></TableRow> :
                  audit.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">{fmtDate(a.timestamp)}</TableCell>
                      <TableCell className="text-sm font-medium">{a.user_name}</TableCell>
                      <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{a.action}</span></TableCell>
                      <TableCell className="text-sm text-slate-500">{a.object_type} {a.object_id ? `· ${String(a.object_id).slice(0, 8)}` : ""}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div></div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{f?.id ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
          {f && (
            <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Nom complet</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="mt-1" data-testid="user-name" /></div>
              <div className="col-span-2"><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required disabled={!!f.id} className="mt-1" data-testid="user-email" /></div>
              <div className="col-span-2"><Label className="text-xs">Mot de passe {f.id && "(laisser vide pour conserver)"}</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required={!f.id} className="mt-1" data-testid="user-password" />{(!f.id || f.password) && <PasswordStrength password={f.password} />}</div>
              <div><Label className="text-xs">Rôle</Label>
                <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs">Département</Label><Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} className="mt-1" /></div>
              <div className="col-span-2 border-t pt-3">
                <details open={!f.id}>
                  <summary className="text-xs font-semibold cursor-pointer text-slate-600 select-none" data-testid="user-perms-section">Modules & droits d'accès (optionnel — défaut : ceux du rôle)</summary>
                  <div className="mt-2 mb-3 rounded-lg border bg-brand-50/40 p-3">
                    <Label className="text-xs font-semibold text-brand-700">Profil type (applique un jeu de droits en 1 clic)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {allProfiles.map((p) => (
                        <Button key={p.value} type="button" variant="outline" size="sm"
                          className="h-7 text-xs bg-white hover:bg-brand-100 border-brand-200"
                          data-testid={`perm-profile-${p.value}`}
                          onClick={() => { setF({ ...f, permissions: expandPerms(p.perms) }); toast.success(`Profil « ${p.label} » appliqué`); }}>
                          {p.label}
                        </Button>
                      ))}
                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-slate-400"
                        data-testid="perm-profile-reset" onClick={() => setF({ ...f, permissions: {} })}>Réinitialiser</Button>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1.5">Vous pouvez ensuite ajuster chaque action ci-dessous. Gérez vos propres profils dans Paramètres → Profils de droits.</p>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1 mb-2">Chaque module est configuré individuellement : cochez les actions autorisées (Voir · Créer · Modifier · Supprimer · Valider · Exporter). Non coché = hérité du rôle.</p>
                  <div className="max-h-72 overflow-y-auto scrollbar-thin pr-1"><PermMatrix value={f.permissions || {}} onChange={(p) => setF({ ...f, permissions: p })} /></div>
                </details>
              </div>
              <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" disabled={saving} className="bg-brand-600 text-white" data-testid="user-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!devU} onOpenChange={(o) => !o && setDevU(null)}>
        <DialogContent className="max-w-lg" data-testid="user-devices-dialog">
          <DialogHeader><DialogTitle className="font-head">Appareils de {devU?.name}</DialogTitle></DialogHeader>
          {devU && <DevicesPanel userId={devU.id} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!permU} onOpenChange={(o) => !o && setPermU(null)}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">Droits par module — {permU?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">« Par défaut » applique les droits du rôle {permU && ROLE_LABELS[permU.role]}. « Personnalisé » permet de cocher chaque action.</p>
          <PermMatrix value={permForm} onChange={setPermForm} />
          <DialogFooter><Button variant="outline" onClick={() => setPermU(null)}>Annuler</Button><Button onClick={savePerm} data-testid="perm-save" className="bg-brand-600 text-white">Enregistrer les droits</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
